import React, {useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {SafetyOutlined} from "@ant-design/icons";
import OfficeAccessModal from "../popups/Header/Profile/OfficeAccessModal";

const ADMIN_LABELS = {
    "permitted-emails": "Permitted Emails",
    "mapped-kvps": "Mapped KVPs",
    "document-labels-mapping": "Document Labels",
    suppliers: "Suppliers",
    "mailbox-users": "Mailbox Users",
    "ocr-configuration": "OCR Configuration",
};

const buildCrumbs = (pathname) => {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 0) return [];

    const [a, b, c, d] = segments;

    if (a === "invoice") return [{label: "Invoices"}];

    if (a === "details") {
        return [
            {label: "Invoices", to: "/invoice"},
            {label: `Invoice · ${b}`},
        ];
    }

    if (a === "emailqueues" && segments.length === 1) {
        return [{label: "Email Queues"}];
    }

    if (a === "emailqueues" && b === "details") {
        return [
            {label: "Email Queues", to: "/emailqueues"},
            {label: `Email · ${c}`},
        ];
    }

    if (a === "report") return [{label: "Reports"}];

    if (a === "admin") {
        if (b === "suppliers" && c === "details") {
            return [
                {label: "Admin"},
                {label: "Suppliers", to: "/admin/suppliers"},
                {label: `Supplier · ${d}`},
            ];
        }
        return [
            {label: "Admin"},
            {label: ADMIN_LABELS[b] || b},
        ];
    }

    return [];
};

const Topbar = ({adminData}) => {
    const location = useLocation();
    const crumbs = buildCrumbs(location.pathname);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTabKey, setModalTabKey] = useState("offices");

    if (crumbs.length === 0) return null;

    const openModal = (tabKey) => {
        setModalTabKey(tabKey);
        setModalOpen(true);
    };

    const officesCount = adminData?.offices?.length ?? 0;
    const fleetsCount = adminData?.fleets?.length ?? 0;
    const accountingCompaniesCount = adminData?.accountingCompanies?.length ?? 0;
    const responsibleVesselsCount = adminData?.vesselResponsibilities?.length ?? 0;

    const stats = [
        {count: officesCount, label: officesCount === 1 ? "office" : "offices", key: "offices"},
        {count: fleetsCount, label: fleetsCount === 1 ? "fleet" : "fleets", key: "fleets"},
        {count: accountingCompaniesCount, label: accountingCompaniesCount === 1 ? "entity" : "entities", key: "entities"},
        {count: responsibleVesselsCount, label: responsibleVesselsCount === 1 ? "vessel" : "vessels", key: "vesselResponsibilities"},
    ];

    return (
        <>
            <div
                className="flex items-center gap-4 px-7 py-3 bg-white border-b border-[#E4E7EE] sticky top-0 z-10"
                style={{minHeight: 52}}
            >
                <div className="flex items-center gap-2 text-[13px] text-[#7A849A] flex-wrap min-w-0">
                    <span>Workspace</span>
                    {crumbs.map((c, i) => {
                        const isLast = i === crumbs.length - 1;
                        return (
                            <React.Fragment key={i}>
                                <span className="text-[#CFD4DF]">/</span>
                                {c.to && !isLast ? (
                                    <Link to={c.to} className="hover:text-[#0B1220]">
                                        {c.label}
                                    </Link>
                                ) : (
                                    <span className={isLast ? "text-[#0B1220] font-semibold" : ""}>
                                        {c.label}
                                    </span>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="flex-1"/>

                {adminData && (
                    <div className="hidden md:flex items-center gap-1.5 text-[12px] text-[#374152] px-3 py-1.5 rounded-full border border-[#E4E7EE] bg-white">
                        <SafetyOutlined className="text-[#7A849A] text-[13px]"/>
                        {stats.map((s, i) => (
                            <React.Fragment key={s.key}>
                                <button
                                    onClick={() => openModal(s.key)}
                                    className="hover:text-[#0B1220] cursor-pointer"
                                    title={`View ${s.label}`}
                                >
                                    <span className="font-semibold tabular-nums">{s.count}</span>{" "}{s.label}
                                </button>
                                {i < stats.length - 1 && <span className="text-[#CFD4DF]">·</span>}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>

            {modalOpen && (
                <OfficeAccessModal
                    visible={modalOpen}
                    data={adminData}
                    tabKey={modalTabKey}
                    onCancel={() => setModalOpen(false)}
                />
            )}
        </>
    );
};

export default Topbar;
